import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["name", "errorText", "disableErrorContainer", "disabled", "readOnly", "useNumberFormat", "prefixDisplay", "suffixDisplay", "className", "$css", "label", "placeholder", "autoComplete", "onBlur", "onFocus", "disableAutoComplete", "children", "collapseError", "labelProps", "labelVariant", "helperText"];
import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import NumberFormat from 'react-number-format';
import { COLORS, TYPOGRAPHY } from '../../../../themes';
import { hover, flex, focusStyles, isKeyboard, cssByBreakpoint } from '../../../../styling';
import { InputError } from '../Error';
import Label from '../../Label';
import { addInputErrorAriaProps, dotToDash } from '../../../../utils';
import { Div } from '../../../Styled';
import { questionnaireContainer } from '../../Questionnaire';
import HelperText from '../../../text/HelperText';
export var InputElementStyles = /*#__PURE__*/css(["width:100%;box-sizing:border-box;padding:10px 16px;border-radius:inherit;flex:1;", ";color:", ";::placeholder{color:", ";}::-moz-placeholder{opacity:1;color:", ";}:focus::placeholder{color:", ";}:focus::-moz-placeholder{opacity:1;color:", ";}", ";", ";:-webkit-autofill::first-line{", ";}:autofill{", ";}"], TYPOGRAPHY.M, COLORS.NEUTRAL_700, COLORS.NEUTRAL_500, COLORS.NEUTRAL_500, COLORS.NEUTRAL_700, COLORS.NEUTRAL_700, function (_ref) {
  var disabled = _ref.disabled;
  return disabled && css(["color:", ";::placeholder{color:", ";}::-moz-placeholder{color:", ";}"], COLORS.NEUTRAL_300, COLORS.NEUTRAL_400, COLORS.NEUTRAL_400);
}, function (_ref2) {
  var readOnly = _ref2.readOnly;
  return readOnly && css(["color:", ";::placeholder{color:", ";}::-moz-placeholder{color:", ";}"], COLORS.PRIMARY_600, COLORS.PRIMARY_100, COLORS.PRIMARY_100);
}, TYPOGRAPHY.M, TYPOGRAPHY.INPUT_SEMIBOLD);
export var InputWrapperStyles = /*#__PURE__*/css(["input{cursor:text;}position:relative;border-radius:4px;", " ", ";", " ", " ", " ", ""], flex('flex-start', 'stretch'), hover('border', "1px solid ".concat(COLORS.NEUTRAL_300), "1px solid ".concat(COLORS.PRIMARY_600)), function (_ref3) {
  var $error = _ref3.$error;
  return $error && css(["border:1px solid ", " !important;"], COLORS.NEGATIVE_500);
}, function (_ref4) {
  var $focus = _ref4.$focus,
      $error = _ref4.$error;
  return $focus && css(["z-index:1;", " ", " box-shadow:0 0 0 4px ", ";"], focusStyles, !$error && "border: 1px solid ".concat(COLORS.FOCUS, ";"), $error ? COLORS.NEGATIVE_500 : COLORS.FOCUS);
}, function (_ref5) {
  var $disabled = _ref5.$disabled;
  return $disabled && css(["pointer-events:none;background:", ";border:1px solid ", " !important;"], COLORS.NEUTRAL_150, COLORS.NEUTRAL_150);
}, function (_ref6) {
  var $readOnly = _ref6.$readOnly;
  return $readOnly && css(["pointer-events:none;background:", ";border:1px solid ", " !important;"], COLORS.PRIMARY_100, COLORS.PRIMARY_100);
});
var InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "BaseInput__InputWrapper",
  componentId: "sc-1wd4zug-0"
})(["", ""], InputWrapperStyles);
export var InputElement = /*#__PURE__*/styled.input.withConfig({
  displayName: "BaseInput__InputElement",
  componentId: "sc-1wd4zug-1"
})(["", ""], InputElementStyles);
var PrefixContainer = /*#__PURE__*/styled(Div).withConfig({
  displayName: "BaseInput__PrefixContainer",
  componentId: "sc-1wd4zug-2"
})(["", " flex:0 0 auto;user-select:none;pointer-events:none;", ";color:", ";", ";", ";", ";"], flex('center', 'center'), TYPOGRAPHY.M, COLORS.NEUTRAL_600, function (_ref7) {
  var $error = _ref7.$error;
  return $error && css(["color:", ";"], COLORS.NEGATIVE_500);
}, function (_ref8) {
  var $disabled = _ref8.$disabled;
  return $disabled && css(["color:", ";"], COLORS.NEUTRAL_300);
}, function (_ref9) {
  var $readOnly = _ref9.$readOnly;
  return $readOnly && css(["color:", ";"], COLORS.PRIMARY_600);
});

var BaseInput = function BaseInput(_ref10) {
  var name = _ref10.name,
      errorText = _ref10.errorText,
      _ref10$disableErrorCo = _ref10.disableErrorContainer,
      disableErrorContainer = _ref10$disableErrorCo === void 0 ? false : _ref10$disableErrorCo,
      disabled = _ref10.disabled,
      readOnly = _ref10.readOnly,
      useNumberFormat = _ref10.useNumberFormat,
      prefixDisplay = _ref10.prefixDisplay,
      suffixDisplay = _ref10.suffixDisplay,
      className = _ref10.className,
      $css = _ref10.$css,
      label = _ref10.label,
      placeholder = _ref10.placeholder,
      autoComplete = _ref10.autoComplete,
      _onBlur = _ref10.onBlur,
      _onFocus = _ref10.onFocus,
      _ref10$disableAutoCom = _ref10.disableAutoComplete,
      disableAutoComplete = _ref10$disableAutoCom === void 0 ? false : _ref10$disableAutoCom,
      children = _ref10.children,
      collapseError = _ref10.collapseError,
      labelProps = _ref10.labelProps,
      _ref10$labelVariant = _ref10.labelVariant,
      labelVariant = _ref10$labelVariant === void 0 ? 'label' : _ref10$labelVariant,
      helperText = _ref10.helperText,
      rest = _objectWithoutProperties(_ref10, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var idRef = useRef(dotToDash(name)).current;
  var isQuestionnaire = labelVariant === 'questionnaire';
  return /*#__PURE__*/React.createElement(Div, {
    $css: $css,
    className: className
  }, /*#__PURE__*/React.createElement(Div, {
    className: "input-container",
    $css: css(["", ""], isQuestionnaire && cssByBreakpoint([css(["", ";.input-wrapper{width:100%;flex:0 0 auto;}"], questionnaireContainer), css([".input-wrapper{width:27.6rem;max-width:50vw;}"])]))
  }, !isQuestionnaire && /*#__PURE__*/React.createElement(Label, _extends({
    $css: css(["margin-bottom:16px;"]),
    disabled: disabled,
    htmlFor: idRef,
    label: label,
    placeholder: placeholder
  }, labelProps)), isQuestionnaire && /*#__PURE__*/React.createElement("label", {
    htmlFor: idRef
  }, label), /*#__PURE__*/React.createElement(InputWrapper, {
    className: "input-wrapper",
    $error: !!errorText,
    $disabled: disabled,
    $focus: focused,
    $readOnly: readOnly
  }, prefixDisplay && /*#__PURE__*/React.createElement(PrefixContainer, {
    $css: css(["padding-left:16px;"]),
    $error: !!errorText,
    $disabled: disabled,
    $readOnly: readOnly
  }, prefixDisplay), /*#__PURE__*/React.createElement(InputElement, _extends({
    "data-test-id": idRef,
    id: idRef
  }, rest, {
    name: name,
    autoComplete: disableAutoComplete && autoComplete ? "new-".concat(autoComplete) : autoComplete,
    placeholder: placeholder,
    $hasPrefix: !!prefixDisplay,
    as: useNumberFormat ? NumberFormat : '',
    disabled: disabled,
    readOnly: readOnly,
    onFocus: function onFocus(e) {
      if (_onFocus) {
        _onFocus(e);
      }

      setFocused(isKeyboard(e));
    },
    onBlur: function onBlur(e) {
      if (_onBlur) _onBlur(e);
      setFocused(false);
    }
  }, addInputErrorAriaProps(errorText, idRef, rest))), suffixDisplay && /*#__PURE__*/React.createElement(PrefixContainer, {
    $css: css(["padding-right:16px;"]),
    $error: !!errorText,
    $disabled: disabled,
    $readOnly: readOnly
  }, suffixDisplay), children)), helperText && !errorText && /*#__PURE__*/React.createElement(HelperText, {
    disabled: disabled,
    $css: css(["display:block;margin-top:8px;"])
  }, helperText), !disableErrorContainer && (!helperText || errorText) && /*#__PURE__*/React.createElement(InputError, {
    $css: css(["", ""], isQuestionnaire && cssByBreakpoint([css(["margin-top:16px;"]), css(["margin-top:8px;"])])),
    className: "input-error",
    errorText: errorText,
    errorId: idRef,
    collapseError: typeof collapseError !== 'undefined' ? collapseError : isQuestionnaire
  }));
};

export default BaseInput;